import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import PageSkeleton from '../../components/dashboard/PageSkeleton';
import SearchableChannelSelect from '../../components/dashboard/SearchableChannelSelect';
import './pokemon.css';
import {
  Loader2, Save, CheckCircle2, AlertCircle,
  Search, X, Trash2, ChevronLeft, ChevronRight,
  Sparkles, Star, Zap, Shield, MapPin, Grid3X3, List, Swords,
  Settings2, ImageIcon, MousePointerClick, Crown,
  ShieldAlert, Radio, MessageSquare, Hash, ToggleLeft, ToggleRight,
} from 'lucide-react';
import {
  searchPokemon, getPokemonSpriteUrl, QUEST_REGIONS,
  type PokemonEntry
} from '../../utils/pokemon/pokemonData';

/* ═══════════════════════════════════════════════════════════════
   POKÉMON CONFIG — compact, modern glass-card dashboard panel
   ═══════════════════════════════════════════════════════════════ */

interface PokemonConfigProps { guildId: string; guildData?: any }

/* ── Type data ── */
const TYPE_DATA: Record<string, { id: string; color: string; glow: string }> = {
  bug:      { id: '1393484357263691786', color: '#A8B820', glow: 'rgba(168,184,32,0.3)' },
  dark:     { id: '1393485321739440250', color: '#705848', glow: 'rgba(112,88,72,0.3)' },
  dragon:   { id: '1393485171277434951', color: '#7038F8', glow: 'rgba(112,56,248,0.3)' },
  electric: { id: '1393484854900953199', color: '#F8D030', glow: 'rgba(248,208,48,0.3)' },
  fairy:    { id: '1393485403721433138', color: '#EE99AC', glow: 'rgba(238,153,172,0.3)' },
  fighting: { id: '1393483195453800518', color: '#C03028', glow: 'rgba(192,48,40,0.3)' },
  fire:     { id: '1393484638655348796', color: '#F08030', glow: 'rgba(240,128,48,0.3)' },
  flying:   { id: '1393483274881470594', color: '#A890F0', glow: 'rgba(168,144,240,0.3)' },
  ghost:    { id: '1393484426268119120', color: '#705898', glow: 'rgba(112,88,152,0.3)' },
  grass:    { id: '1393484742556385331', color: '#78C850', glow: 'rgba(120,200,80,0.3)' },
  ground:   { id: '1393484161427177512', color: '#E0C068', glow: 'rgba(224,192,104,0.3)' },
  ice:      { id: '1393485114767315056', color: '#98D8D8', glow: 'rgba(152,216,216,0.3)' },
  normal:   { id: '1393483098414514192', color: '#A8A878', glow: 'rgba(168,168,120,0.3)' },
  poison:   { id: '1393483349724762254', color: '#A040A0', glow: 'rgba(160,64,160,0.3)' },
  psychic:  { id: '1393484975826796575', color: '#F85888', glow: 'rgba(248,88,136,0.3)' },
  rock:     { id: '1393484295875596328', color: '#B8A038', glow: 'rgba(184,160,56,0.3)' },
  steel:    { id: '1393484592320745513', color: '#B8B8D0', glow: 'rgba(184,184,208,0.3)' },
  water:    { id: '1393484684784173136', color: '#6890F0', glow: 'rgba(104,144,240,0.3)' },
};

const QUEST_EMOJI: Record<string, string> = {
  kanto: '1399813352188022874', johto: '1399813178292047942', hoenn: '1399809005106364417',
  sinnoh: '1399809190922551338', unova: '1399809440978571425', kalos: '1399813047228436602',
  alola: '1399808752546484254', galar: '1399813588671004752', paldea: '1399813934814331112',
};

const REGION_COLORS: Record<string, string> = {
  kanto: '#E74C3C', johto: '#F1C40F', hoenn: '#2ECC71', sinnoh: '#3498DB',
  unova: '#9B59B6', kalos: '#E67E22', alola: '#1ABC9C', galar: '#E91E63', paldea: '#FF6B9D',
};

function emojiUrl(id: string) { return `https://cdn.discordapp.com/emojis/${id}.webp?size=96&quality=lossless`; }

const PER_PAGE = 18;
const ALL_TYPES = Object.keys(TYPE_DATA).sort();
const ALL_REGIONS = QUEST_REGIONS.map(r => r.toLowerCase()).sort();

const VARIANT_PREFIX: Record<string, string> = {
  alola: 'Alolan', galar: 'Galarian', hisui: 'Hisuian', paldea: 'Paldean',
  mega: 'Mega', gmax: 'Gigantamax', primal: 'Primal', kalos: 'Kalosian',
};

function fmtName(n: string) {
  if (n.includes('-')) {
    const parts = n.split('-');
    const base = parts[0].charAt(0).toUpperCase() + parts[0].slice(1);
    const form = parts.slice(1).join('-');
    const prefix = VARIANT_PREFIX[form.toLowerCase()];
    if (prefix) return `${prefix} ${base}`;
    return `${base} ${form.charAt(0).toUpperCase() + form.slice(1)}`;
  }
  return n.charAt(0).toUpperCase() + n.slice(1);
}

/* ── Sprite (gradient backdrop, not a flat square) ── */
function Sprite({ name, size = 48, className: cls, shiny }: { name: string; size?: number; className?: string; shiny?: boolean }) {
  const [err, setErr] = useState(false);
  if (err) return (
    <span className={`pkm-sprite-fallback ${cls || ''}`} style={{ width: size, height: size, fontSize: size * 0.4 }}>?</span>
  );
  return (
    <img
      src={getPokemonSpriteUrl(name)}
      alt={name}
      width={size}
      height={size}
      className={`pkm-sprite ${shiny ? 'pkm-sprite-shiny' : ''} ${cls || ''}`}
      style={{ imageRendering: 'pixelated' }}
      onError={() => setErr(true)}
    />
  );
}

/* ── Pokémon Search Bar ── */
function PokemonSearch({ onSelect, placeholder, disabled }: { onSelect: (p: PokemonEntry) => void; placeholder?: string; disabled?: boolean }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState<PokemonEntry[]>([]);
  const [open, setOpen] = useState(false);
  const [focused, setFocused] = useState(-1);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    q.length >= 1 ? (setResults(searchPokemon(q, 8)), setOpen(true), setFocused(-1)) : (setResults([]), setOpen(false));
  }, [q]);

  useEffect(() => {
    const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!open || results.length === 0) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setFocused(f => Math.min(f + 1, results.length - 1)); }
    if (e.key === 'ArrowUp') { e.preventDefault(); setFocused(f => Math.max(f - 1, 0)); }
    if (e.key === 'Enter' && focused >= 0) { e.preventDefault(); onSelect(results[focused]); setQ(''); setOpen(false); }
    if (e.key === 'Escape') setOpen(false);
  };

  return (
    <div ref={ref} className="pkm-search">
      <div className="pkm-search-wrap">
        <Search className="pkm-search-icon" />
        <input
          type="text"
          value={q}
          disabled={disabled}
          onChange={e => setQ(e.target.value)}
          onFocus={() => q.length >= 1 && setOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder || 'Search Pokémon...'}
          className="pkm-search-input"
        />
        {q && <button onClick={() => { setQ(''); setOpen(false); }} className="pkm-search-clear"><X className="w-3.5 h-3.5" /></button>}
      </div>
      {open && results.length > 0 && (
        <div className="pkm-search-dropdown">
          {results.map((p, i) => (
            <button
              key={p.name}
              type="button"
              onClick={() => { onSelect(p); setQ(''); setOpen(false); }}
              className={`pkm-search-result ${focused === i ? 'focused' : ''}`}
            >
              <Sprite name={p.name} size={28} />
              <span className="pkm-search-result-name">{p.displayName}</span>
              <span className="pkm-search-result-id">#{String(p.id).padStart(3, '0')}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Shared form primitives (match Leveling/Moderation dashboard pages) ── */
function Toggle({ enabled, onChange }: { enabled: boolean; onChange: (v: boolean) => void }) {
  return (
    <button type="button" onClick={() => onChange(!enabled)} className="transition-colors shrink-0">
      {enabled ? <ToggleRight className="w-7 h-7 text-primary" /> : <ToggleLeft className="w-7 h-7 text-gray-700" />}
    </button>
  );
}

function ToggleRow({ title, description, enabled, onChange }: { title: string; description: string; enabled: boolean; onChange: (v: boolean) => void }) {
  return (
    <div className="flex items-center justify-between gap-4 py-2.5">
      <div className="min-w-0">
        <p className="text-sm text-gray-200">{title}</p>
        <p className="text-gray-600 text-[11px] mt-0.5">{description}</p>
      </div>
      <Toggle enabled={enabled} onChange={onChange} />
    </div>
  );
}

function SectionCard({
  icon: Icon, title, description, children, iconClass = 'bg-primary/10 text-primary',
}: {
  icon: any; title: string; description: string; children?: React.ReactNode; iconClass?: string;
}) {
  return (
    <div className="border rounded-xl overflow-hidden bg-dark-800/40 border-white/[0.06]">
      <div className="flex items-center gap-3 px-5 py-4 border-b border-white/[0.04]">
        <div className={`p-2 rounded-lg ${iconClass}`}>
          <Icon className="w-4 h-4" />
        </div>
        <div className="text-left">
          <p className="text-[14px] font-semibold text-white">{title}</p>
          <p className="text-gray-500 text-[12px]">{description}</p>
        </div>
      </div>
      <div className="px-5 pb-5 pt-3 space-y-1 divide-y divide-white/[0.03]">
        {children}
      </div>
    </div>
  );
}

function RoleSelect({ value, onChange, roles, placeholder = 'No role set' }: { value: string; onChange: (v: string) => void; roles: any[]; placeholder?: string }) {
  return (
    <div className="relative">
      <Crown className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-500 pointer-events-none" />
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-dark-700/60 border border-white/[0.07] rounded-lg pl-8 pr-3 py-2 text-sm text-white appearance-none focus:outline-none focus:border-primary/40 cursor-pointer"
      >
        <option value="">{placeholder}</option>
        {roles.map((r: any) => (
          <option key={r.id} value={r.id}>@{r.name}</option>
        ))}
      </select>
    </div>
  );
}

/* Multi-channel picker: searchable add + removable chips below */
function MultiChannelSelect({ selected, onChange, channels }: { selected: string[]; onChange: (v: string[]) => void; channels: any[] }) {
  const available = channels.filter((c: any) => !selected.includes(c.id));
  return (
    <div className="space-y-2.5">
      <SearchableChannelSelect
        value={null}
        onChange={(id) => { if (id) onChange([...selected, id]); }}
        channels={available}
        placeholder="Add a protected channel…"
      />
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {selected.map((id) => {
            const ch = channels.find((c: any) => c.id === id);
            return (
              <span key={id} className="inline-flex items-center gap-1.5 bg-dark-700/80 text-gray-300 text-xs pl-2.5 pr-1.5 py-1 rounded-lg border border-white/[0.06]">
                <Hash className="w-3 h-3 text-gray-500" />
                {ch ? ch.name : id}
                <button onClick={() => onChange(selected.filter((s) => s !== id))} className="text-gray-600 hover:text-red-400 transition-colors p-0.5">
                  <X className="w-3 h-3" />
                </button>
              </span>
            );
          })}
        </div>
      )}
      {selected.length === 0 && (
        <p className="text-gray-600 text-[11px]">No channels protected yet — thefts anywhere go unpunished.</p>
      )}
    </div>
  );
}

interface ServerConfigState {
  images_enabled: boolean;
  buttons_enabled: boolean;
  rare_enabled: boolean;
  regional_enabled: boolean;
  cl_enabled: boolean;
  sh_enabled: boolean;
  type_enabled: boolean;
  quest_enabled: boolean;
  naming_enabled: boolean;
  only_pings: boolean;
  grind_mode: boolean;
  rare_role: string;
  regional_role: string;
}

const SERVER_CONFIG_DEFAULTS: ServerConfigState = {
  images_enabled: true, buttons_enabled: true, rare_enabled: true, regional_enabled: true,
  cl_enabled: true, sh_enabled: true, type_enabled: true, quest_enabled: true,
  naming_enabled: true, only_pings: false, grind_mode: false,
  rare_role: '', regional_role: '',
};

interface ProtectionState {
  shiny_channels: string[];
  collection_channels: string[];
  shiny_log_channel: string;
  collection_log_channel: string;
  shiny_ping_phrase: string;
}

const PROTECTION_DEFAULTS: ProtectionState = {
  shiny_channels: [], collection_channels: [],
  shiny_log_channel: '', collection_log_channel: '',
  shiny_ping_phrase: '**:sparkles: Shiny Hunt Pings:**',
};

/* ═══════════════════════════════════════════════════════════════ */
/* ══ MAIN COMPONENT ════════════════════════════════════════════ */
/* ═══════════════════════════════════════════════════════════════ */

export default function PokemonConfig({ guildId, guildData }: PokemonConfigProps) {
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [hasChanges, setHasChanges] = useState(false);

  const [clList, setClList] = useState<string[]>([]);
  const [shTarget, setShTarget] = useState<string>('');
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [selectedRegions, setSelectedRegions] = useState<string[]>([]);
  const [serverConfig, setServerConfig] = useState<ServerConfigState>(SERVER_CONFIG_DEFAULTS);
  const [protection, setProtection] = useState<ProtectionState>(PROTECTION_DEFAULTS);
  const [clPage, setClPage] = useState(0);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [activeTab, setActiveTab] = useState<'collection' | 'shiny' | 'types' | 'quests' | 'settings' | 'protection'>('collection');

  const channels = guildData?.channels || [];
  const roles = guildData?.roles || [];

  const originalRef = useRef<{
    cl: string[]; sh: string; types: string[]; regions: string[];
    server: ServerConfigState; protection: ProtectionState;
  }>({ cl: [], sh: '', types: [], regions: [], server: SERVER_CONFIG_DEFAULTS, protection: PROTECTION_DEFAULTS });

  const markDirty = useCallback(() => {
    const o = originalRef.current;
    const dirty =
      JSON.stringify(clList) !== JSON.stringify(o.cl) ||
      shTarget !== o.sh ||
      JSON.stringify(selectedTypes.sort()) !== JSON.stringify(o.types.sort()) ||
      JSON.stringify(selectedRegions.sort()) !== JSON.stringify(o.regions.sort()) ||
      JSON.stringify(serverConfig) !== JSON.stringify(o.server) ||
      JSON.stringify(protection) !== JSON.stringify(o.protection);
    setHasChanges(dirty);
  }, [clList, shTarget, selectedTypes, selectedRegions, serverConfig, protection]);

  useEffect(() => { markDirty(); }, [markDirty]);

  /* ── Load from MongoDB via API ── */
  useEffect(() => {
    if (!guildId || !token) return;
    const userId = user?.id || '1124389055598170182'; // Fallback to dev ID
    const apiUrl = `${(import.meta.env.VITE_API_URL || '')}/api/dashboard/${guildId}/pokemon-config?user_id=${userId}`;
    (async () => {
      try {
        const res = await fetch(apiUrl, { headers: { Authorization: `Bearer ${token}` } });
        if (res.ok) {
          const d = await res.json();
          const cl = d._user_collection || [];
          const sh = d._user_shiny || '';
          const tp = d._user_types || [];
          const rg = d._user_regions || [];
          const server: ServerConfigState = {
            images_enabled: d.images_enabled ?? true,
            buttons_enabled: d.buttons_enabled ?? true,
            rare_enabled: d.rare_enabled ?? true,
            regional_enabled: d.regional_enabled ?? true,
            cl_enabled: d.cl_enabled ?? true,
            sh_enabled: d.sh_enabled ?? true,
            type_enabled: d.type_enabled ?? true,
            quest_enabled: d.quest_enabled ?? true,
            naming_enabled: d.naming_enabled ?? true,
            only_pings: d.only_pings ?? false,
            grind_mode: d.grind_mode ?? false,
            rare_role: d.rare_role ? String(d.rare_role) : '',
            regional_role: d.regional_role ? String(d.regional_role) : '',
          };
          const prot: ProtectionState = {
            shiny_channels: (d.shiny_channels || []).map(String),
            collection_channels: (d.collection_channels || []).map(String),
            shiny_log_channel: d.shiny_log_channel ? String(d.shiny_log_channel) : '',
            collection_log_channel: d.collection_log_channel ? String(d.collection_log_channel) : '',
            shiny_ping_phrase: d.shiny_ping_phrase || PROTECTION_DEFAULTS.shiny_ping_phrase,
          };
          setClList(cl);
          setShTarget(sh);
          setSelectedTypes(tp);
          setSelectedRegions(rg);
          setServerConfig(server);
          setProtection(prot);
          originalRef.current = { cl: [...cl], sh, types: [...tp], regions: [...rg], server, protection: prot };
        }
      } catch (err) { console.error('[PokemonConfig] Fetch error:', err); }
      finally { setLoading(false); }
    })();
  }, [guildId, token, user]);

  /* ── Save to MongoDB via API ── */
  async function handleSave() {
    if (!token) return;
    setSaving(true); setSaveStatus('idle');
    const userId = user?.id || '1124389055598170182'; // Fallback to dev ID
    try {
      const apiUrl = `${(import.meta.env.VITE_API_URL || '')}/api/dashboard/${guildId}/pokemon-config?user_id=${userId}`;
      const res = await fetch(apiUrl, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          _user_collection: clList,
          _user_shiny: shTarget,
          _user_types: selectedTypes,
          _user_regions: selectedRegions,
          ...serverConfig,
          rare_role: serverConfig.rare_role || null,
          regional_role: serverConfig.regional_role || null,
          ...protection,
          shiny_log_channel: protection.shiny_log_channel || null,
          collection_log_channel: protection.collection_log_channel || null,
        }),
      });
      if (res.ok) {
        setSaveStatus('success');
        originalRef.current = {
          cl: [...clList], sh: shTarget, types: [...selectedTypes], regions: [...selectedRegions],
          server: serverConfig, protection,
        };
        setHasChanges(false);
      } else {
        setSaveStatus('error');
      }
    } catch { setSaveStatus('error'); }
    finally { setSaving(false); setTimeout(() => setSaveStatus('idle'), 3000); }
  }

  /* ── Collection helpers ── */
  const addToCollection = (p: PokemonEntry) => {
    if (clList.length < 250 && !clList.includes(p.name)) {
      const n = [...clList, p.name];
      setClList(n);
      setClPage(Math.floor((n.length - 1) / PER_PAGE));
    }
  };

  const removeFromCollection = (idx: number) => {
    const n = clList.filter((_, j) => j !== idx);
    setClList(n);
    if (clPage > 0 && clPage >= Math.ceil(n.length / PER_PAGE)) setClPage(clPage - 1);
  };

  const updateServer = <K extends keyof ServerConfigState>(key: K, value: ServerConfigState[K]) =>
    setServerConfig((prev) => ({ ...prev, [key]: value }));

  const updateProtection = <K extends keyof ProtectionState>(key: K, value: ProtectionState[K]) =>
    setProtection((prev) => ({ ...prev, [key]: value }));

  if (loading) {
    return <PageSkeleton rows={4} />;
  }

  const totalPages = Math.max(1, Math.ceil(clList.length / PER_PAGE));
  const pagedCl = clList.slice(clPage * PER_PAGE, (clPage + 1) * PER_PAGE);

  const tabs = [
    { key: 'collection' as const, label: 'Collection', icon: Grid3X3, count: clList.length },
    { key: 'shiny'      as const, label: 'Shiny Hunt', icon: Sparkles,  count: shTarget ? 1 : 0 },
    { key: 'types'      as const, label: 'Types',      icon: Shield,    count: selectedTypes.length },
    { key: 'quests'     as const, label: 'Quests',     icon: MapPin,    count: selectedRegions.length },
    { key: 'settings'   as const, label: 'Spawn Settings', icon: Settings2, count: 0 },
    { key: 'protection' as const, label: 'Protection', icon: ShieldAlert, count: protection.shiny_channels.length + protection.collection_channels.length },
  ];

  return (
    <div className="pkm-config">

      {/* ── Page header ── */}
      <div className="pkm-header">
        <div className="pkm-header-left">
          <div className="pkm-header-icon">
            <Swords className="w-5 h-5" />
          </div>
          <div>
            <h2 className="pkm-header-title">Pokémon Settings</h2>
            <p className="pkm-header-sub">Collection, shiny hunt, spawn config & theft protection</p>
          </div>
        </div>
        <button
          onClick={handleSave}
          disabled={saving || !hasChanges}
          className={`pkm-save-btn ${
            saveStatus === 'success' ? 'pkm-save-success' :
            saveStatus === 'error'   ? 'pkm-save-error'   :
            hasChanges               ? 'pkm-save-active'  : 'pkm-save-idle'
          }`}
        >
          {saving            ? <Loader2     className="w-3.5 h-3.5 animate-spin" /> :
           saveStatus === 'success' ? <CheckCircle2 className="w-3.5 h-3.5" /> :
           saveStatus === 'error'   ? <AlertCircle  className="w-3.5 h-3.5" /> :
                                      <Save         className="w-3.5 h-3.5" />}
          <span>{saving ? 'Saving…' : saveStatus === 'success' ? 'Saved!' : saveStatus === 'error' ? 'Error' : 'Save'}</span>
        </button>
      </div>

      {/* ── Stats strip ── */}
      <div className="pkm-stats">
        {[
          { icon: Grid3X3,  label: 'Collected',   value: `${clList.length}/250`,        color: '#6890F0' },
          { icon: Sparkles, label: 'Shiny Hunt',  value: shTarget ? fmtName(shTarget) : 'None', color: '#F8D030' },
          { icon: Zap,      label: 'Type Pings',  value: selectedTypes.length,           color: '#F08030' },
          { icon: MapPin,   label: 'Quest Pings', value: selectedRegions.length,         color: '#78C850' },
        ].map(({ icon: Icon, label, value, color }) => (
          <div key={label} className="pkm-stat" style={{ '--sc': color } as React.CSSProperties}>
            <div className="pkm-stat-icon"><Icon className="w-3.5 h-3.5" /></div>
            <div>
              <p className="pkm-stat-val">{value}</p>
              <p className="pkm-stat-label">{label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* ── Pill tabs ── */}
      <div className="pkm-tabs">
        {tabs.map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`pkm-tab ${activeTab === tab.key ? 'pkm-tab-active' : ''}`}
          >
            <tab.icon className="w-3.5 h-3.5" />
            <span>{tab.label}</span>
            {tab.count > 0 && <span className="pkm-tab-badge">{tab.count}</span>}
          </button>
        ))}
      </div>

      {/* ── Tab content ── */}
      <div className="pkm-panel">

        {/* Collection */}
        {activeTab === 'collection' && (
          <div>
            <div className="pkm-panel-toolbar">
              <PokemonSearch
                placeholder="Add Pokémon to collection…"
                disabled={clList.length >= 250}
                onSelect={addToCollection}
              />
              <div className="pkm-toolbar-right">
                {clList.length > 0 && (
                  <button onClick={() => { setClList([]); setClPage(0); }} className="pkm-clear-btn">
                    <Trash2 className="w-3.5 h-3.5" /> Clear
                  </button>
                )}
                <div className="pkm-view-toggle">
                  <button onClick={() => setViewMode('grid')} className={`pkm-view-btn ${viewMode === 'grid' ? 'active' : ''}`}><Grid3X3 className="w-3.5 h-3.5" /></button>
                  <button onClick={() => setViewMode('list')} className={`pkm-view-btn ${viewMode === 'list' ? 'active' : ''}`}><List className="w-3.5 h-3.5" /></button>
                </div>
              </div>
            </div>

            {clList.length > 0 ? (
              <>
                {viewMode === 'grid' ? (
                  <div className="pkm-grid">
                    {pagedCl.map((name, i) => {
                      const idx = clPage * PER_PAGE + i;
                      return (
                        <div key={name} className="pkm-card" style={{ animationDelay: `${i * 20}ms` }}>
                          <button className="pkm-card-remove" onClick={() => removeFromCollection(idx)} title="Remove">
                            <X className="w-3 h-3" />
                          </button>
                          <div className="pkm-card-sprite-wrap">
                            <Sprite name={name} size={44} />
                          </div>
                          <span className="pkm-card-name">{fmtName(name)}</span>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="pkm-list">
                    {pagedCl.map((name, i) => {
                      const idx = clPage * PER_PAGE + i;
                      return (
                        <div key={name} className="pkm-list-item" style={{ animationDelay: `${i * 15}ms` }}>
                          <span className="pkm-list-num">{idx + 1}</span>
                          <Sprite name={name} size={26} />
                          <span className="pkm-list-name">{fmtName(name)}</span>
                          <button onClick={() => removeFromCollection(idx)} className="pkm-list-remove"><X className="w-3.5 h-3.5" /></button>
                        </div>
                      );
                    })}
                  </div>
                )}

                {totalPages > 1 && (
                  <div className="pkm-pagination">
                    <button disabled={clPage === 0} onClick={() => setClPage(clPage - 1)} className="pkm-page-btn"><ChevronLeft className="w-4 h-4" /></button>
                    <span className="pkm-page-info">{clPage + 1} / {totalPages}</span>
                    <button disabled={clPage >= totalPages - 1} onClick={() => setClPage(clPage + 1)} className="pkm-page-btn"><ChevronRight className="w-4 h-4" /></button>
                  </div>
                )}
              </>
            ) : (
              <div className="pkm-empty">
                <Star className="w-8 h-8 text-gray-700" />
                <p className="pkm-empty-title">No Pokémon added yet</p>
                <p className="pkm-empty-sub">Search above to add Pokémon you want to be pinged for</p>
              </div>
            )}
          </div>
        )}

        {/* Shiny Hunt */}
        {activeTab === 'shiny' && (
          <div>
            {shTarget ? (
              <div className="pkm-shiny-card">
                <div className="pkm-shiny-sprite-wrap">
                  <Sprite name={shTarget} size={80} shiny />
                </div>
                <div className="pkm-shiny-info">
                  <p className="pkm-shiny-name">{fmtName(shTarget)}</p>
                  <p className="pkm-shiny-desc">✨ You'll be notified when this Pokémon spawns shiny</p>
                  <button onClick={() => setShTarget('')} className="pkm-shiny-remove">
                    <X className="w-3.5 h-3.5" /> Remove Hunt
                  </button>
                </div>
              </div>
            ) : (
              <div>
                <PokemonSearch placeholder="Set your shiny hunt target…" onSelect={p => setShTarget(p.name)} />
                <div className="pkm-empty">
                  <Sparkles className="w-8 h-8 text-yellow-700" />
                  <p className="pkm-empty-title">No active shiny hunt</p>
                  <p className="pkm-empty-sub">Search for a Pokémon to start hunting its shiny variant</p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Type Pings */}
        {activeTab === 'types' && (
          <div>
            {selectedTypes.length > 0 && (
              <div className="pkm-panel-toolbar" style={{ marginBottom: 12 }}>
                <p className="text-gray-500 text-[12px]">{selectedTypes.length} type{selectedTypes.length !== 1 ? 's' : ''} selected</p>
                <button onClick={() => setSelectedTypes([])} className="pkm-clear-btn ml-auto">
                  <Trash2 className="w-3.5 h-3.5" /> Clear all
                </button>
              </div>
            )}
            <div className="pkm-type-grid">
              {ALL_TYPES.map(type => {
                const { id, color, glow } = TYPE_DATA[type];
                const on = selectedTypes.includes(type);
                return (
                  <button
                    key={type}
                    type="button"
                    onClick={() => setSelectedTypes(on ? selectedTypes.filter(t => t !== type) : [...selectedTypes, type])}
                    className={`pkm-type-btn ${on ? 'pkm-type-active' : ''}`}
                    style={{ '--tc': color, '--tg': glow } as React.CSSProperties}
                  >
                    <img src={emojiUrl(id)} alt="" className="pkm-type-emoji" />
                    <span className="pkm-type-label">{type}</span>
                    {on && <span className="pkm-type-check">✓</span>}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Quest Pings */}
        {activeTab === 'quests' && (
          <div>
            {selectedRegions.length > 0 && (
              <div className="pkm-panel-toolbar" style={{ marginBottom: 12 }}>
                <p className="text-gray-500 text-[12px]">{selectedRegions.length} region{selectedRegions.length !== 1 ? 's' : ''} selected</p>
                <button onClick={() => setSelectedRegions([])} className="pkm-clear-btn ml-auto">
                  <Trash2 className="w-3.5 h-3.5" /> Clear all
                </button>
              </div>
            )}
            <div className="pkm-region-grid">
              {ALL_REGIONS.map(region => {
                const on = selectedRegions.includes(region);
                const eid = QUEST_EMOJI[region];
                const rColor = REGION_COLORS[region] || '#6890F0';
                return (
                  <button
                    key={region}
                    type="button"
                    onClick={() => setSelectedRegions(on ? selectedRegions.filter(v => v !== region) : [...selectedRegions, region])}
                    className={`pkm-region-btn ${on ? 'pkm-region-active' : ''}`}
                    style={{ '--rc': rColor } as React.CSSProperties}
                  >
                    {eid && <img src={emojiUrl(eid)} alt="" className="pkm-region-emoji" />}
                    <span className="pkm-region-label">{region}</span>
                    {on && <span className="pkm-region-check">✓</span>}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Spawn Settings (server-level) */}
        {activeTab === 'settings' && (
          <div className="space-y-4">
            <SectionCard icon={ImageIcon} title="Spawn Display" description="How Pokémon spawns look in this server">
              <ToggleRow title="Spawn images" description="Show the Pokémon's image on spawn embeds" enabled={serverConfig.images_enabled} onChange={(v) => updateServer('images_enabled', v)} />
              <ToggleRow title="Catch buttons" description="Show quick-catch buttons under spawns" enabled={serverConfig.buttons_enabled} onChange={(v) => updateServer('buttons_enabled', v)} />
              <ToggleRow title="Pokémon naming" description="Reveal the spawned Pokémon's name in chat" enabled={serverConfig.naming_enabled} onChange={(v) => updateServer('naming_enabled', v)} />
              <ToggleRow title="Only pings, no extra text" description="Send ping mentions only, skip flavor text" enabled={serverConfig.only_pings} onChange={(v) => updateServer('only_pings', v)} />
              <ToggleRow title="Grind mode" description="Minimal embeds for high-volume grinding channels" enabled={serverConfig.grind_mode} onChange={(v) => updateServer('grind_mode', v)} />
            </SectionCard>

            <SectionCard icon={MousePointerClick} title="Ping Categories" description="Which ping types are active in this server">
              <ToggleRow title="Collection pings" description="Ping members hunting for a specific Pokémon" enabled={serverConfig.cl_enabled} onChange={(v) => updateServer('cl_enabled', v)} />
              <ToggleRow title="Shiny pings" description="Ping members hunting for a shiny" enabled={serverConfig.sh_enabled} onChange={(v) => updateServer('sh_enabled', v)} />
              <ToggleRow title="Type pings" description="Ping members subscribed to a Pokémon type" enabled={serverConfig.type_enabled} onChange={(v) => updateServer('type_enabled', v)} />
              <ToggleRow title="Quest pings" description="Ping members subscribed to a region's quests" enabled={serverConfig.quest_enabled} onChange={(v) => updateServer('quest_enabled', v)} />
            </SectionCard>

            <SectionCard icon={Crown} title="Special Roles" description="Auto-pinged roles for rare & regional spawns">
              <ToggleRow title="Rare Pokémon pings" description="Ping the rare role when a rare spawn appears" enabled={serverConfig.rare_enabled} onChange={(v) => updateServer('rare_enabled', v)} />
              <div className="pt-3">
                <label className="block text-xs text-gray-400 mb-1.5">Rare Pokémon role</label>
                <RoleSelect value={serverConfig.rare_role} onChange={(v) => updateServer('rare_role', v)} roles={roles} />
              </div>
              <ToggleRow title="Regional Pokémon pings" description="Ping the regional role when a regional spawn appears" enabled={serverConfig.regional_enabled} onChange={(v) => updateServer('regional_enabled', v)} />
              <div className="pt-3">
                <label className="block text-xs text-gray-400 mb-1.5">Regional Pokémon role</label>
                <RoleSelect value={serverConfig.regional_role} onChange={(v) => updateServer('regional_role', v)} roles={roles} />
              </div>
            </SectionCard>
          </div>
        )}

        {/* Theft Protection */}
        {activeTab === 'protection' && (
          <div className="space-y-4">
            <SectionCard icon={Sparkles} title="Shiny Protection" description="Only the tagged shiny hunter may catch — others get timed out" iconClass="bg-yellow-500/10 text-yellow-500">
              <div className="pt-1">
                <label className="block text-xs text-gray-400 mb-1.5">Protected channels</label>
                <MultiChannelSelect selected={protection.shiny_channels} onChange={(v) => updateProtection('shiny_channels', v)} channels={channels} />
              </div>
              <div className="pt-4">
                <label className="block text-xs text-gray-400 mb-1.5">Theft log channel</label>
                <SearchableChannelSelect
                  value={protection.shiny_log_channel || null}
                  onChange={(v) => updateProtection('shiny_log_channel', v || '')}
                  channels={channels}
                  placeholder="No log channel set"
                />
              </div>
              <div className="pt-4">
                <label className="block text-xs text-gray-400 mb-1.5 flex items-center gap-1.5"><MessageSquare className="w-3 h-3" /> Shiny ping phrase</label>
                <input
                  type="text"
                  value={protection.shiny_ping_phrase}
                  onChange={(e) => updateProtection('shiny_ping_phrase', e.target.value)}
                  maxLength={200}
                  className="w-full bg-dark-700/60 border border-white/[0.06] rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-primary/40"
                />
              </div>
            </SectionCard>

            <SectionCard icon={Grid3X3} title="Collection Protection" description="Only tagged collectors may catch — others get timed out" iconClass="bg-blue-500/10 text-blue-500">
              <div className="pt-1">
                <label className="block text-xs text-gray-400 mb-1.5">Protected channels</label>
                <MultiChannelSelect selected={protection.collection_channels} onChange={(v) => updateProtection('collection_channels', v)} channels={channels} />
              </div>
              <div className="pt-4">
                <label className="block text-xs text-gray-400 mb-1.5">Theft log channel</label>
                <SearchableChannelSelect
                  value={protection.collection_log_channel || null}
                  onChange={(v) => updateProtection('collection_log_channel', v || '')}
                  channels={channels}
                  placeholder="No log channel set"
                />
              </div>
            </SectionCard>

            <div className="flex items-start gap-2.5 px-1 text-gray-600 text-[11px] leading-relaxed">
              <Radio className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <p>Stealing a protected catch times the thief out (15m → 12h, escalating per offense within 3 days) and logs the incident if a log channel is set.</p>
            </div>
          </div>
        )}
      </div>

      {/* Floating unsaved bar */}
      {hasChanges && (
        <div className="pkm-unsaved">
          <span>Unsaved changes</span>
          <button onClick={handleSave} disabled={saving} className="pkm-unsaved-save">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            Save Now
          </button>
        </div>
      )}
    </div>
  );
}
